import { useState } from 'react';
import { Copy, Check, RefreshCw, Shield, Clock, Sparkles, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface EmailAddressProps {
  email: string;
  timeRemaining: { hours: number; minutes: number; expired: boolean };
  onRefresh: () => void;
  onGenerateNew: () => void;
  onSendTest: () => void;
  isLoading: boolean;
}

export const EmailAddress = ({
  email,
  timeRemaining,
  onRefresh,
  onGenerateNew,
  onSendTest,
  isLoading,
}: EmailAddressProps) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      toast.success('Email copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const formatTime = () => {
    if (timeRemaining.expired) return 'Expired';
    if (timeRemaining.hours > 0) {
      return `${timeRemaining.hours}h ${timeRemaining.minutes}m`;
    }
    return `${timeRemaining.minutes}m`;
  };

  const getTimeColor = () => {
    if (timeRemaining.expired) return 'text-destructive';
    if (timeRemaining.hours === 0 && timeRemaining.minutes < 30) return 'text-warning';
    return 'text-muted-foreground';
  };

  return (
    <div className="glass-premium rounded-3xl p-6 lg:p-8 animate-slide-up glow-border hover-lift">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="absolute -inset-1 gradient-bg rounded-2xl opacity-60 blur-sm animate-pulse-slow" />
            <div className="relative w-12 h-12 rounded-xl gradient-bg flex items-center justify-center shadow-glow animate-float">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
          </div>
          <div>
            <span className="text-lg font-bold gradient-text">Your Private Email</span>
            <p className="text-xs text-muted-foreground animate-fade-in">Secure & disposable</p>
          </div>
        </div>
        <div className={`flex items-center gap-2.5 px-4 py-2 rounded-full bg-secondary/80 border border-border/60 ${getTimeColor()} hover-glow transition-all`}>
          <Clock className="w-4 h-4 animate-wiggle" />
          <span className="text-sm font-semibold">Expires in {formatTime()}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        <div className="flex-1 relative group">
          <div 
            onClick={copyToClipboard}
            className="flex items-center gap-4 bg-secondary/70 hover:bg-secondary rounded-2xl px-6 py-5 cursor-pointer transition-all duration-300 border-2 border-border/50 hover:border-primary/50 hover:shadow-glow group rainbow-border"
          >
            <span className="font-mono text-lg sm:text-xl font-bold text-foreground tracking-wider flex-1 select-all truncate">
              {email}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0 h-10 w-10 rounded-xl hover:bg-primary/20"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard();
              }}
            >
              {copied ? (
                <Check className="w-5 h-5 text-success animate-scale-pulse" />
              ) : (
                <Copy className="w-5 h-5 group-hover:text-primary transition-colors" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={onRefresh}
            disabled={isLoading}
            className="shrink-0 h-14 w-14 rounded-xl border-2 border-border/60 hover:border-primary/50 hover:bg-primary/10 hover-lift transition-all"
            aria-label="Refresh inbox"
          >
            <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>

          <Button
            variant="outline"
            onClick={onSendTest}
            disabled={isLoading}
            className="shrink-0 h-14 rounded-xl border-2 border-border/60 hover:border-accent/50 hover:bg-accent/10 px-5 hover-lift transition-all"
            aria-label="Send test email"
          >
            <Mail className="w-5 h-5 mr-2" />
            Test
          </Button>

          <Button
            onClick={onGenerateNew}
            className="shrink-0 h-14 rounded-xl gradient-bg text-primary-foreground shadow-glow hover:shadow-glow-intense transition-all duration-300 px-6 hover-lift animate-scale-pulse font-bold"
          >
            <Sparkles className="w-5 h-5 mr-2 animate-wiggle" />
            New Address
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mt-5 flex items-center gap-2 animate-fade-in">
        <Shield className="w-4 h-4 text-primary animate-pulse" />
        Your inbox is secured with a unique token stored only in your browser
      </p>
    </div>
  );
};